import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatInTz, LENGTHS, STATUS_META, TIMEZONES, utcToZonedParts, zonedToUtc } from "@/lib/schedule";

export type QueueRow = {
  id: string;
  topic: string;
  scheduled_at: string;
  timezone: string;
  status: string;
  post_id: string | null;
  error: string | null;
  schedule_id: string | null;
  length_words: number;
  publish_automatically: boolean;
};

export type QueueEdit = {
  topic: string;
  scheduled_at: string;
  timezone: string;
  length_words: number;
  publish_automatically: boolean;
};

function StatusPill({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? STATUS_META.scheduled;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs uppercase tracking-wider ${meta.className}`}>
      <span aria-hidden>{meta.icon}</span> {meta.label}
    </span>
  );
}

function EditDialog({
  row,
  onClose,
  onSave,
}: {
  row: QueueRow | null;
  onClose: () => void;
  onSave: (row: QueueRow, values: QueueEdit) => Promise<void> | void;
}) {
  const [topic, setTopic] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [timezone, setTimezone] = useState("America/New_York");
  const [length, setLength] = useState(1500);
  const [autoPublish, setAutoPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!row) return;
    const parts = utcToZonedParts(row.scheduled_at, row.timezone);
    setTopic(row.topic);
    setDate(parts.date);
    setTime(parts.time);
    setTimezone(row.timezone);
    setLength(row.length_words);
    setAutoPublish(row.publish_automatically);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    await onSave(row, {
      topic: topic.trim(),
      scheduled_at: zonedToUtc(date, time, timezone).toISOString(),
      timezone,
      length_words: length,
      publish_automatically: autoPublish,
    });
    setBusy(false);
  };

  return (
    <Dialog open={!!row} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit scheduled post</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="q-topic" className="text-xs uppercase tracking-wider text-muted-foreground">Topic</Label>
            <Input id="q-topic" className="mt-1.5" value={topic} onChange={(e) => setTopic(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="q-date" className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
              <Input id="q-date" type="date" className="mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="q-time" className="text-xs uppercase tracking-wider text-muted-foreground">Time</Label>
              <Input id="q-time" type="time" className="mt-1.5" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="q-tz" className="text-xs uppercase tracking-wider text-muted-foreground">Timezone</Label>
              <select
                id="q-tz"
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="q-len" className="text-xs uppercase tracking-wider text-muted-foreground">Length</Label>
              <select
                id="q-len"
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={length}
                onChange={(e) => setLength(Number(e.target.value))}
              >
                {LENGTHS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={autoPublish} onCheckedChange={(v) => setAutoPublish(!!v)} />
            Publish automatically
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QueueList({
  rows,
  emptyText,
  onCancel,
  onRestore,
  onDelete,
  onEdit,
}: {
  rows: QueueRow[];
  emptyText: string;
  onCancel?: (row: QueueRow) => void;
  onRestore?: (row: QueueRow) => void;
  onDelete?: (row: QueueRow) => void;
  onEdit?: (row: QueueRow, values: QueueEdit) => Promise<void> | void;
}) {
  const [editing, setEditing] = useState<QueueRow | null>(null);

  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{emptyText}</p>;

  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.id} className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <StatusPill status={r.status} />
              <span className="text-xs text-muted-foreground">
                {r.schedule_id ? "From schedule" : "One-off"} · {r.length_words} words · {r.publish_automatically ? "auto-publish" : "save as draft"}
              </span>
            </div>
            <div className="truncate font-medium">{r.topic}</div>
            <div className="text-xs text-muted-foreground">
              {formatInTz(r.scheduled_at, r.timezone)} · {r.timezone}
            </div>
            {r.error && <div className="mt-1 text-xs text-destructive">{r.error}</div>}
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {r.post_id && (
              <Link to={`/admin/blog/${r.post_id}`}>
                <Button variant="outline" size="sm">Open post</Button>
              </Link>
            )}
            {onEdit && (
              <Button variant="outline" size="sm" onClick={() => setEditing(r)}>Edit</Button>
            )}
            {onRestore && !["scheduled", "processing"].includes(r.status) && (
              <Button variant="outline" size="sm" onClick={() => onRestore(r)}>Move to upcoming</Button>
            )}
            {onCancel && r.status === "scheduled" && (
              <Button variant="ghost" size="sm" onClick={() => onCancel(r)}>Cancel</Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="sm" onClick={() => onDelete(r)}>Delete</Button>
            )}
          </div>
        </div>
      ))}

      {onEdit && (
        <EditDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSave={async (row, values) => {
            await onEdit(row, values);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
